import { runnerFrameComponentModule } from '../../components/runner-frame/runner-frame';
import { multiFileChallengeServiceFileManagementModule } from './multi-file-challenge-service-file-management';
import { multiFileChallengeServiceInteractModule } from './multi-file-challenge-service-interact';
import { onConfirmComponentModule } from '../../components/on-confirm/on-confirm';
import { addUnique, remove } from '../../utils/array-utils';
import {
  cleanPath,
  parentPath,
  splitPath,
} from '../../utils/file-utils/file-utils';
import { eventEmitter, stopEvent } from '../../utils/event-utils';
import { challengeModelModule } from '../../models/challenge';
import { codeFileModelModule } from '../../models/code-file';
import { isFunction } from '../../utils/type-utils';

export const multiFileChallengeServiceModule = angular
  .module('Q.services.multi-file-challenge-service', [
    multiFileChallengeServiceFileManagementModule,
    multiFileChallengeServiceInteractModule,
    runnerFrameComponentModule,
    onConfirmComponentModule,
    challengeModelModule,
    codeFileModelModule,
  ])

  .directive('linkMultiFileChallenges', function () {
    return {
      restrict: 'A',
      require: 'linkMultiFileChallenges',
      controller: function ($scope, $attrs) {
        const ctrl = this;
        let _svc; // keep track of the last one
        const getSvc = () => {
          const svc = $scope.$eval($attrs.linkMultiFileChallenges);
          if (svc && svc !== _svc) {
            _svc = svc;
            svc.fileTrees = svc.fileTrees || [];
          }
          return svc || _svc;
        };

        $scope.$watch(getSvc, (newSvc, oldSvc) => {
          if (newSvc && oldSvc && newSvc !== oldSvc) {
            let refresh = false;
            // relink trees and layout
            if (oldSvc.fileTrees) {
              oldSvc.fileTrees.forEach(ctrl.linkFileTree);
              refresh = oldSvc.fileTrees.length;
            }
            if (oldSvc.layout) {
              ctrl.linkLayout(oldSvc.layout);
              refresh = true;
            }
            if (refresh && newSvc.updateAfterFileChanges) {
              newSvc.updateAfterFileChanges();
            }
            // clear old links
            oldSvc.fileTrees = [];
            oldSvc.fileLayout = null;
          }
        });

        ctrl.linkFileTree = (fileTree) => {
          const svc = getSvc();
          if (!svc) return;
          fileTree.dragEnabled = true;
          addUnique(svc.fileTrees, fileTree);
          if (
            fileTree.files === svc.files ||
            fileTree.files === svc.referenceFiles
          ) {
            // can re-order via drag-and-drop for these trees
            fileTree.onFileDrop = ({
              file,
              path,
              dropPath,
              dropDirectory,
              event,
            }) => {
              stopEvent(event);
              let newPath = cleanPath(dropPath);
              if (!dropDirectory) {
                newPath = parentPath(newPath);
              }
              newPath += '/' + splitPath(path).filename;
              svc.rename(
                file,
                path,
                fileTree.files === svc.referenceFiles,
                newPath,
              );
            };
          } // otherwise just drag to layout

          fileTree.onFileDragStart = ({ file }) => {
            if (file && file.directory !== true && svc.fileLayout) {
              svc.fileLayout.startDragging(file);
            }
          };
          fileTree.onFileDragStop = () => {
            if (svc.fileLayout) {
              svc.fileLayout.stopDragging();
            }
          };
        };
        ctrl.unlinkFileTree = (fileTree) => {
          const svc = getSvc();
          if (!svc) return;
          remove(svc.fileTrees, fileTree);
        };
        ctrl.linkLayout = (layout) => {
          const svc = getSvc();
          if (!svc) return;
          if (svc.fileLayout !== layout) {
            svc.fileLayout = layout;

            layout.onTabDragStart = ({ file }) => {
              if (file && file.path && !file.$deleted && !file.locked) {
                svc.fileTrees.forEach((ft) => {
                  ft.startDragging(file);
                });
              }
            };
            layout.onFileDragStop = () => {
              svc.fileTrees.forEach((ft) => {
                ft.stopDragging();
              });
            };
          }
        };
      },
    };
  })

  .factory(
    'multiFileChallengeService',
    function (
      $timeout,
      runnerFrameService,
      $rootScope,
      $q,
      $state,
      multiFileChallengeServiceFM,
      multiFileChallengeServiceInteract,
      Challenge,
      CodeFile,
      $log,
    ) {
      let mfcsCounter = 0;

      /**
       * Sets up common multi-file challenge controls
       * @param {*?} opts.svc - If provided, add methods to this object, otherwise add to a new object
       * @param {Challenge} opts.challenge - Required for configuration
       * @param {boolean?} opts.editingChallenge - If true, then we are editing a challenge (used to always set up SmartIDE for the editor no matter the challenge config)
       * @param {Solution?} opts.solution - If provided, configure in solution mode & use solution as the base
       * @param {String?} opts.docsArticleId - If provided, add the KB panel with this article
       * @param {Array>?} opts.docsArticleList - If provided, adds an article list to the KB pane
       * @param {String?} opts.docsArticleListTitle - Sets title of article list
       * @param {String?} opts.docsArticleListContent - Adds some markdown above the article list
       * @param {boolean?} opts.review - If true, configure for review mode
       * @param {String|Function?} opts.toolbarTemplate - If string, the toolbar for files with a path.
       *      If a function, will be passed in the file object.
       * @param {Function?} opts.getRunFiles - If provided, this function is used to determine which files to include when running.
       *      Otherwise, only test files are included.
       * @param {Function<file>?} opts.afterFileChanges - If provided, will be called when a file is added, renamed/moved, or destroyed
       *
       * @returns {*} Configured service object
       */
      function create(opts = {}) {
        const {
          svc = {},
          challenge,
          solution,
          challengeConfig,
          review: reviewMode,
          enableAiAgent,
        } = opts;

        $log.info('This assessment will have an AI agent', enableAiAgent);

        svc._id = ++mfcsCounter; // used to uniquely identify mfcs's
        svc.challenge = svc.challenge || challenge;
        svc.challenge.options = svc.challenge.options || {};
        svc.solution = svc.solution || solution;
        if (svc.solution) {
          svc.solution.options = svc.solution.options || {};
        }
        svc.challengeConfig = challengeConfig || [];

        if (!svc.on) {
          eventEmitter(svc);
        }
        svc.updateWebPreview = (force) => {
          if (!svc.expired && svc.webPreviewFile) {
            // re-render preview
            svc.webPreviewFile.sync(force);
          }
        };
        svc.updateFilePaths = () => {
          const model = svc.solution || svc.challenge;
          if (model.files && model.files.updatePaths) {
            const renamedFiles = model.files.updatePaths();
            if (svc.workspace) {
              renamedFiles.forEach(({ file, oldPath }) => {
                svc.workspace.renameFile(file, oldPath);
                // notify others
                if (svc.interact && svc.interact.canEdit) {
                  svc.interact.renameFile({ oldPath, newPath: file.path });
                }
              });
            }
          }
        };
        svc.onFileChange = ({ force, file, userInitiated } = {}) => {
          const { cursor, selections } = file || {};
          svc.updateWebPreview(force);
          if (file && userInitiated) {
            // TODO: filter out cursor-only changes?
            svc.updateFilePaths();
          }
          if (svc.updateAfterFileChanges) {
            svc.updateAfterFileChanges();
          }
          svc.emit('fileChange', { file, userInitiated, cursor, selections });
        };

        svc.onTabFocused = ({ file } = {}) => {
          svc.emit('tabFocused', { file });
        };

        Object.defineProperties(svc, {
          files: {
            get() {
              return (
                (svc.solution ? svc.solution.files : svc.challenge.files) || []
              );
            },
            configurable: true,
          },
          referenceFiles: {
            get() {
              return svc.solution ? null : svc.challenge.referenceFiles || [];
            },
            configurable: true,
          },
          allFiles: {
            get() {
              return [].concat(svc.files, svc.referenceFiles || []);
            },
            configurable: true,
          },
          challengeOptions: {
            get() {
              if (svc.solution) {
                return (svc.solution.options = svc.solution.options || {});
              } else if (svc.challenge) {
                return (svc.challenge.options = svc.challenge.options || {});
              } else {
                return {};
              }
            },
            configurable: true,
          },
        });

        svc.fileTrees = svc.fileTrees || [];

        /**
         * Handles the asynchronous nature of having a fileLayout controller. The callback is called
         * when we finally have a fileLayout controller available (if ever)
         * @param {Function} cb - callback with the fileLayout controller as the first argument
         * @param {boolean} changeState - If false, then only call the callback if we already have it.
         */
        svc.withFileLayout = (cb, changeState = true) => {
          if (changeState && opts.layoutState && !$state.is(opts.layoutState)) {
            // clear the layout control so we can reload it
            svc.fileLayout = null;
            let unlisten = $rootScope.$on('$stateChangeSuccess', () => {
              unlisten();
              $timeout(svc.withFileLayout.bind(svc, cb, false));
            });
            // switch to the proper state for editing files if necessary
            if ($state.current && $state.current.name && !$state.transition) {
              $state.go(opts.layoutState);
            }
            return;
          }
          if (svc.fileLayout) {
            cb(svc.fileLayout);
          }
        };
        svc.openFile = (file) => {
          if (file) {
            svc.withFileLayout((layout) => {
              layout.open(file);
            });
          }
        };
        svc.openInitialFile = (file) => {
          $timeout(() => {
            svc.openFile(file || svc.files[0]);
          }, 50);
        };

        setupLayoutLink(svc, opts);
        multiFileChallengeServiceFM.setupFileManagement(svc, opts);
        multiFileChallengeServiceInteract.setupInteract(svc);
        addChallengeEditorTools(svc, opts);
        if (!reviewMode) {
          linkVirtualFiles(svc, svc.challengeConfig);
          linkVirtualFiles(svc, svc.challengeEditorTools);
        }

        svc.refreshOpenFiles = () => {
          if (svc.openFiles) {
            // fix references to changed yet open files
            const allFiles = svc.allFiles;
            const oldOpenFiles = svc.openFiles.slice();
            svc.openFiles.length = 0;
            oldOpenFiles.forEach((file) => {
              if (file && file.$type && file.path && !file.$deleted) {
                // verify matching files by type & ID (preferred) or path
                file = allFiles.find(
                  (f) =>
                    f.$type === file.$type &&
                    ((f.id && f.id === file.id) || f.path === file.path),
                );
              }
              if (file) {
                // add unique to prevent ng-repeater errors
                addUnique(svc.openFiles, file);
              }
            });
          }
        };

        setupFileListeners(svc);

        return svc;
      }

      function setupLayoutLink(svc, opts) {
        svc.clickFile = (file) => {
          if (file && isFunction(file.clickAction)) {
            file.clickAction();
          } else if (
            file &&
            file !== svc.docsArticleFile &&
            file.docsArticleId &&
            svc.showHelp
          ) {
            svc.showHelp(file.docsArticleId);
          } else if (
            file &&
            (!file.directory || file.directory === 'openable') &&
            !file.disabled
          ) {
            if (file.docsArticleId && svc.setHelpArticle) {
              svc.setHelpArticle(file.docsArticleId);
            }
            svc.openFile(file);
          }
          svc.fileTrees.forEach((fileTree) => {
            if (fileTree && fileTree.files.includes(file)) {
              fileTree.select(file);
            }
          });
        };

        svc.getToolbarTemplate = (file) =>
          file.toolbarTemplate || (file.path && opts.toolbarTemplate);
      }

      function addChallengeEditorTools(svc, opts) {
        if (!svc.runnerFrameFile) {
          svc.runnerFrameFile = {
            id: 'runnerframe',
            icon: 'transport-play',
            label: 'Run Output',
            tooltip: 'View the output from running code',
            template: opts.runnerFrameTemplate || 'runnerFrame',
            tabset: 'bottomRight',
            get initialResponse() {
              return svc.runResponse;
            },
            get hidden() {
              // web-only project challenges (previews on, candidate tests off)
              // have nothing to show here
              return (
                svc.challenge.disableIdeFeatures ||
                (!svc.challenge.candidateTestsEnabled &&
                  !svc.webPreviewFile.hidden)
              );
            },
          };
        }
        if (!svc.webPreviewFile) {
          svc.webPreviewFile = {
            id: 'webpreview',
            path: 'webpreview',
            icon: 'globe',
            label: 'Web Preview',
            tooltip: 'View a real-time preview of this web application',
            template: opts.webPreviewTemplate || 'webPreview',
            directory: 'openable',
            tabset: 'topRight',
            get responsiveMode() {
              return svc.challenge && svc.challenge.responsiveMode();
            },
            get uniqueKey() {
              return svc.solution ? svc.solution.id : svc.challenge.id;
            },
            get files() {
              return svc._getRunFiles(false, true);
            },
            get console() {
              return svc.webPreviewConsoleFile.console;
            },
            get hidden() {
              // simply hide this if disabled
              return (
                !(svc.challenge && svc.challenge.webPreviews()) ||
                svc.challenge.disableIdeFeatures
              );
            },
            sync: angular.noop,
            setSyncFunction(fn) {
              svc.webPreviewFile.sync = fn || angular.noop;
            },
            openConsole() {
              svc.clickFile(svc.webPreviewConsoleFile);
            },
          };
          svc.webPreviewConsoleFile = {
            id: 'webpreviewconsole',
            path: 'webpreview/console',
            icon: 'lang-shell',
            label: 'Web Console',
            tooltip: 'View the console output from the web preview',
            template: opts.webPreviewConsoleTemplate || 'webPreviewConsole',
            lastTabset: 'bottomRight',
            get hidden() {
              return (
                svc.webPreviewFile.hidden || svc.challenge.disableIdeFeatures
              );
            },
            console: [],
          };
        }
        svc.chatAgentFile = {
          id: 'chatagent',
          icon: 'z-action-ai',
          label: 'AI Assistant',
          tooltip: 'Get help from the AI assistant',
          template: 'chatagent',
          lastTabset: 'topRight',
          defaultTabset: 'topRight',
          closeable: false,
        };
        svc.ideSettingsFile = {
          id: 'idesettings',
          icon: 'cog',
          label: 'IDE Settings',
          tooltip: 'Configure how the code editor works',
          template: 'ideOpts',
          hide: {
            layout: true,
            fileMap: !svc.challenge.isAdvancedCode(),
          },
          get hidden() {
            return svc.challenge.disableIdeFeatures;
          },
        };
        if (svc.challenge.disableIdeFeatures) {
          svc.challengeEditorTools = [];
        } else {
          svc.challengeEditorTools = [
            svc.runnerFrameFile,
            svc.webPreviewFile,
            svc.webPreviewConsoleFile,
            svc.ideSettingsFile,
          ].filter(angular.identity);
        }
        if (opts.enableAiAgent) {
          svc.challengeEditorTools.push(svc.chatAgentFile);
        }

        if (
          (opts.docsArticleId || opts.docsArticleList) &&
          !svc.challenge.disableIdeFeatures
        ) {
          svc.docsArticleFile = {
            id: 'help',
            icon: 'help',
            label: 'IDE Help',
            path: 'docs',
            template: 'docsPane',
            directory: opts.docsAdditionalArticles && 'openable',
            docsHomeArticle: opts.docsArticleId,
            docsArticleId: opts.docsArticleId,
            $docsArticleId: opts.docsArticleId,
            docsArticleList: opts.docsArticleList,
            docsArticleListTitle: opts.docsArticleListTitle,
            docsArticleListContent: opts.docsArticleListContent,
            defaultTabset: 'topRight',
          };
          svc.challengeEditorTools.push(svc.docsArticleFile);
          svc.setHelpArticle = (articleId) => {
            svc.docsArticleFile.$docsArticleId = articleId;
          };
          svc.showHelp = (articleId) => {
            svc.clickFile(svc.docsArticleFile);
            svc.setHelpArticle(articleId);
          };
          if (opts.docsAdditionalArticles) {
            opts.docsAdditionalArticles.forEach((article) => {
              article.$draggable = false;
              svc.challengeEditorTools.push(article);
            });
          }
        }

        if (opts.editChallengeEditorTools) {
          opts.editChallengeEditorTools(svc);
        }
        setupRunnerFrame(svc, svc.runnerFrameFile, opts);
        setupActionsToolbar(svc);
      }

      function setupRunnerFrame(svc, runnerFrameFile, opts) {
        svc.loadRunResponse = (response) => {
          svc.runResponse = response;
          svc.withFileLayout((fileLayout) => {
            // force the frame to refresh
            runnerFrameFile.forceRefresh = Date.now();
            fileLayout.open(runnerFrameFile);
          });
        };
        svc._getRunFiles = (includeEncrypted = true, forWebPreview = false) => {
          let files;
          if (opts.getRunFiles) {
            files = opts.getRunFiles(svc, forWebPreview);
          } else {
            files = Challenge.getRunFiles(svc.challenge, svc);
          }
          if (!includeEncrypted) {
            files = files.filter(
              (f) =>
                !f.encrypted &&
                f.$access !== 'restricted' &&
                f.$access !== 'hidden',
            );
          }
          return files;
        };
        svc.run = () => {
          if (svc.expired) return $q.reject();

          if (svc.webPreviewFile) {
            // always re-render on run
            svc.webPreviewFile.sync();
          }

          return $q((resolve, reject) => {
            svc.withFileLayout((fileLayout) => {
              // for autoruns, we don't activate (focus) the run tab when web previews are visible
              const activateTab =
                !svc.isAutoRun || !fileLayout.find(svc.webPreviewFile);
              svc.isAutoRun = false;

              runnerFrameFile.closeable =
                runnerFrameFile.closeable && 'disabled';
              let delay = 0;
              if (!fileLayout.find(runnerFrameFile)) {
                delay = 2000;
              }
              fileLayout.open(runnerFrameFile, {
                activate: activateTab || delay !== 0,
              });

              // restrict to the important bits
              const files = svc._getRunFiles().map((f) => ({
                path: f.path,
                contents: f.contents,
                encrypted: f.encrypted,
              }));

              $timeout(() => {
                runnerFrameService
                  .run(
                    angular.extend(
                      { privilegeMode: svc.privilegedMode || 'full' },
                      svc.challenge.runConfig,
                      { files },
                    ),
                  )
                  .finally(() => {
                    runnerFrameFile.closeable =
                      runnerFrameFile.closeable && true;
                  })
                  .then((response) => {
                    // store in case we close and reopen the runner frame
                    svc.runResponse = response;
                    svc.setRunResultStatus();
                    return response;
                  })
                  .then(resolve, reject);
              }, delay);
            });
          });
        };

        svc.setRunResultStatus = (response = svc.runResponse) => {
          let status = '';
          if (response && response.result) {
            const result = svc.runResponse.result;
            const passed = Math.max(result.passed, 0) || 0;
            const errors = Math.max(result.errors, 0) || 0;
            const failed = Math.max(result.failed, 0) || 0;
            if (passed > 0 && !errors && !failed) {
              status = 'success';
            } else if (errors || !passed) {
              status = 'danger';
            } else if (failed) {
              status = 'warning';
            } // else, nothing passed, nothing failed
          }
          runnerFrameFile.status = status;
        };

        svc.setRunResultStatus();
      }

      function setupActionsToolbar(svc) {
        if (!svc.actions) {
          const canEditLayout = !svc.challenge.lockedLayout();
          svc.actions = [
            svc.workspaceStatusAction,
            canEditLayout && {
              icon: 'plus',
              toolbarTooltip: 'New Project File',
              onToolbarClick: () => svc.addFile(),
            },
            canEditLayout && { sep: true },
            svc.ideSettingsFile,
            svc.docsArticleFile,
          ].filter(angular.identity);
        }
      }

      /**
       * linkVirtualFiles is a utility function that dynamically binds file state
       * to challenge options using property descriptors.
       * It enables persistent UI state (like tab placement and activation)
       * for special virtual files by syncing their properties (tabset, tabindex, active)
       * with a shared options object.
       * @param {*} svc
       * @param {*} fileArray
       */
      function linkVirtualFiles(svc, fileArray) {
        fileArray.forEach((f) => {
          if (f.id) {
            ['tabset', 'tabindex', 'active'].forEach((field) => {
              const key = (f.id + '$' + field).toLowerCase();
              // If the file has a default value for the field, set it in the options object
              if (f[field]) {
                svc.challengeOptions[key] = f[field];
              }
              Object.defineProperty(f, field, {
                get() {
                  return svc.challengeOptions[key];
                },
                set(val) {
                  svc.challengeOptions[key] = val;
                },
                enumerable: false,
              });
            });
          }
        });
      }

      let restoreFocusTimer;

      function restoreOpenFiles(svc) {
        $timeout.cancel(restoreFocusTimer);
        svc.openFiles = [];

        function openWith(openFn) {
          if (svc.solution) {
            svc.solution.files.forEach(openFn);
            svc.filesModel = svc.solution;
          } else if (svc.challenge) {
            svc.filesModel = svc.challenge;
            svc.challenge.files.forEach(openFn);
            svc.challenge.referenceFiles.forEach(openFn);
          }
          svc.challengeConfig.forEach(openFn);
          svc.challengeEditorTools.forEach(openFn);
        }

        openWith((file) => {
          if (file.tabset && !file.disabled && !file.hidden) {
            svc.openFiles.push(file);
          }
        });

        if (svc.openFiles.length === 0) {
          let focusOnFile;
          openWith((file) => {
            if (file.defaultTabset && !file.disabled && !file.hidden) {
              file.tabset = file.defaultTabset;
              file.tabindex = file.defaultTabindex;
              file.active = file.defaultActive;
              if (file.defaultActive && (!focusOnFile || !focusOnFile.path)) {
                // focus on the most useful file first
                focusOnFile = file;
              }
              svc.openFiles.push(file);
            }
          });

          return (restoreFocusTimer = $timeout(() => {
            // make sure this file is still open
            // (kinda lame, but better than dealing with the async issues)
            if (svc.openFiles.includes(focusOnFile)) {
              svc.clickFile(focusOnFile);
            }
          }, 500));
        }

        return $q.when();
      }

      /**
       * Shallow listeners are setup to track when the underlying data arrays are changed
       */
      function setupFileListeners(svc) {
        let refFilesDebounce;
        svc.refreshOnFilesChanged = () => {
          $timeout.cancel(refFilesDebounce);
          refFilesDebounce = $timeout(() => {
            svc.refreshOpenFiles();

            if (svc.solution) {
              CodeFile.configureAccessLevels(svc.solution.files);
            }

            svc.updateAfterFileChanges();

            // update the layout & file trees
            [svc.fileLayout].concat(svc.fileTrees).forEach((controller) => {
              if (controller && controller.refresh) {
                controller.refresh();
              }
            });
          }, 50);
        };

        const oldDestroy = svc.destroy || angular.noop;
        const listeners = (svc._mfcListeners = [
          $rootScope.$watchCollection(
            () => svc.openFiles,
            svc.refreshOnFilesChanged,
          ),
          $rootScope.$watchCollection(
            () => svc.files,
            svc.refreshOnFilesChanged,
          ),
          $rootScope.$watchCollection(
            () => svc.referenceFiles,
            svc.refreshOnFilesChanged,
          ),
        ]);
        svc.destroy = (...args) => {
          listeners.forEach((fn) => {
            fn();
          });
          if (svc.off) {
            svc.off(true);
          }
          if (svc.workspace) {
            svc.workspace.destroy();
          }
          oldDestroy.apply(svc, ...args);
          svc.destroyed = true;
        };
      }

      return {
        create,
        restoreOpenFiles,
      };
    },
  );
